// ปุ่ม/ส่วนแสดงผลของระบบสมาชิก ที่เอาไปวางในธีมเดิม (navbar, แถบล่างมือถือ, ปุ่มลอย)
import React, { useEffect, useRef, useState } from 'react';
import { useStore, settingOf, openPanel, refreshUser, logout, openLogin } from './store';
import { t } from './i18n';
import { useLang } from '../site/i18n';
import LangSwitcher from '../site/LangSwitcher';
import { PANELS } from './panels';
import './memberhub.css';

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// โลโก้จาก API (tblsetting[0].imglogo) — ถ้า API ยังไม่ตอบ ใช้รูปเดิม
export function SiteLogo({ fallback, className = 'custom-logo', alt = '', ...rest }) {
  const store = useStore();
  const logo = settingOf(store.setting).logo;
  // ยังโหลดโลโก้จริงไม่เสร็จ -> โชว์สปินเนอร์ ไม่โชว์โลโก้เก่า (BOSSY)
  if (!logo) {
    return <span className="mta-spin mta-spin-logo" aria-label="loading" />;
  }
  return <img src={logo} className={className} alt={alt} decoding="async" {...rest} />;
}

function MemberMenu({ lang, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);
  return (
    <div className="mh-menu" ref={ref} role="menu">
      <div className="mh-menu-lang"><LangSwitcher /></div>
      {Object.keys(PANELS).map((k) => (
        <button key={k} type="button" role="menuitem" onClick={() => { onClose(); openPanel(k); }}>
          {t(lang, PANELS[k].label)}
        </button>
      ))}
      <button type="button" role="menuitem" className="mh-menu-logout" onClick={logout}>{t(lang, 'logout')}</button>
    </div>
  );
}

// ส่วนขวาของ navbar — markup เดียวกับเว็บตัวอย่าง (.loginregishead > button.btn.btn-primary > ... > a)
// ให้ CSS ของธีมจัดหน้าตาเอง (ขอบทองไล่สี มุมโค้ง 12px)
function ThemeBtn({ children, onClick, href, className = '', ...rest }) {
  const inner = (
    <div className="-text-container">
      <div className="textwidget">
        <span>{children}</span>
      </div>
    </div>
  );
  // ถ้ามี href ให้เป็นลิงก์จริง (กัน <a> ซ้อนใน <button> ที่กดแล้วไม่ไป)
  if (href) {
    return (
      <a href={href} className={'btn btn-primary mt-lg-3 mr-lg-3 ' + className} role="button" onClick={onClick} {...rest}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" className={'btn btn-primary mt-lg-3 mr-lg-3 ' + className} onClick={onClick} {...rest}>
      {inner}
    </button>
  );
}

export function NavAuth() {
  const { user } = useStore();
  const { lang } = useLang();
  const [menu, setMenu] = useState(false);
  const [spin, setSpin] = useState(false);

  if (!user.loginstate) {
    return (
      <>
        <LangSwitcher />
        <ThemeBtn className="nav-login-btn" onClick={openLogin}>{t(lang, 'login')}</ThemeBtn>
      </>
    );
  }
  return (
    <>
      <span className="mh-top-lang"><LangSwitcher /></span>
      <ThemeBtn className="mh-balance-btn" title={user.username}
        onClick={() => { setSpin(true); refreshUser(); setTimeout(() => setSpin(false), 800); }}
        aria-label={t(lang, 'mh_refresh')}>
        <span className="mh-bal-amount">{fmt(user.balance)}</span>
        <span className={'mh-bal-refresh' + (spin ? ' is-spin' : '')} aria-hidden="true">⟳</span>
      </ThemeBtn>
      <ThemeBtn className="nav-deposit-btn" onClick={() => openPanel('deposit')}><i className="fas fa-wallet" style={{ marginRight: 6 }}></i>{t(lang, 'deposit')}</ThemeBtn>
      <ThemeBtn className="nav-withdraw-btn" onClick={() => openPanel('withdraw')}><i className="fas fa-money-bill-wave" style={{ marginRight: 6 }}></i>{t(lang, 'withdraw')}</ThemeBtn>
      <div className="mh-menu-wrap">
        <ThemeBtn className="mh-member-btn" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)}>
          <i className="fas fa-user mh-member-icon"></i>
          <span className="mh-member-label"> {t(lang, 'mh_member_menu')}</span>
        </ThemeBtn>
        {menu && <MemberMenu lang={lang} onClose={() => setMenu(false)} />}
      </div>
    </>
  );
}

// แถบล่างมือถือ (ฝั่งซ้าย): ยังไม่ login = เข้าสู่ระบบ/สมัคร, login แล้ว = ฝาก/ถอน
export function MobileAuthActions({ loginIcon, registerIcon }) {
  const { user } = useStore();
  const { lang } = useLang();
  const item = (label, icon, onClick, href) => (
    <span className="-item-wrapper">
      <span className="-ic-img">
        <span className="-textfooter d-block">
          {href ? <a href={href}>{label}</a> : <a role="button" onClick={onClick}>{label}</a>}
        </span>
        {href
          ? <a href={href}><img width={50} height={50} src={icon} alt="" style={{ maxWidth: '100%', height: 'auto' }} /></a>
          : <a role="button" onClick={onClick}><img width={50} height={50} src={icon} alt="" style={{ maxWidth: '100%', height: 'auto' }} /></a>}
      </span>
    </span>
  );
  if (!user.loginstate) {
    return (
      <div className="-left-wrapper">
        {item(t(lang, 'login'), loginIcon, openLogin)}
        {item(t(lang, 'register'), registerIcon, null, '/register')}
      </div>
    );
  }
  return (
    <div className="-left-wrapper">
      {item(t(lang, 'deposit'), loginIcon, () => openPanel('deposit'))}
      {item(t(lang, 'withdraw'), registerIcon, () => openPanel('withdraw'))}
    </div>
  );
}

// ปุ่มลอยด้านขวา: กงล้อ เช็คอิน ชวนเพื่อน (+ เมนูสมาชิกบนมือถือ)
export function QuickDock() {
  const store = useStore();
  const { lang } = useLang();
  const [menu, setMenu] = useState(false);
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem('mh_dock_open') !== '0'; } catch (e) { return true; }
  });
  const toggleDock = () => {
    setOpen((o) => {
      const n = !o;
      try { localStorage.setItem('mh_dock_open', n ? '1' : '0'); } catch (e) { }
      if (!n) setMenu(false);
      return n;
    });
  };
  const s = settingOf(store.setting);
  const items = [
    s.wheelOn || !store.setting ? { k: 'wheel', icon: '🎡' } : null,
    s.cardOn ? { k: 'cardgame', icon: '🃏' } : null,
    { k: 'checkin', icon: '📅' },
    { k: 'affiliate', icon: '👥' },
    { k: 'promotion', icon: '🎁' },
  ].filter(Boolean);
  return (
    <div className={'mh-dock ' + (open ? 'mh-dock-open' : 'mh-dock-collapsed')} aria-label={t(lang, 'mh_member_menu')}>
      <button type="button" className="mh-dock-toggle" onClick={toggleDock}
        aria-expanded={open} aria-label={open ? 'ซ่อนเมนู' : 'แสดงเมนู'} title={open ? 'ซ่อนเมนู' : 'แสดงเมนู'}>
        <span className="mh-dock-toggle-ic" aria-hidden="true">{open ? '✕' : '☰'}</span>
      </button>
      {open && items.map((it) => (
        <button key={it.k} type="button" className="mh-dock-btn" onClick={() => openPanel(it.k)}>
          <span className="mh-dock-ic" aria-hidden="true">{it.icon}</span>
          <span className="mh-dock-label">{t(lang, PANELS[it.k].label)}</span>
        </button>
      ))}
      {open && store.user.loginstate && (
        <div className="mh-menu-wrap mh-dock-more">
          <button type="button" className="mh-dock-btn" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)}>
            <span className="mh-dock-ic" aria-hidden="true">☰</span>
            <span className="mh-dock-label">{t(lang, 'mh_more')}</span>
          </button>
          {menu && <MemberMenu lang={lang} onClose={() => setMenu(false)} />}
        </div>
      )}
    </div>
  );
}
